import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, Check, Users, Briefcase, UserCheck, ChevronDown, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const PermissionsDialog = ({ projectId, onUpdate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState({
    can_view_project: [],
    can_view_pictures: [],
    can_view_notes: [],
    revoked_view_project: [],
    revoked_view_pictures: [],
    revoked_view_notes: []
  });
  const [loading, setLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({
    employee: true,
    client: true,
    sub_contractor: true,
    supervisor: true
  });

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      fetchCurrentPermissions();
    }
  }, [isOpen]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      // Filter out admins from the list (they have full access by default)
      setUsers(response.data.filter(u => u.role !== 'admin'));
    } catch (error) {
      toast.error('Failed to fetch users');
    }
  };

  const fetchCurrentPermissions = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`);
      setPermissions({
        can_view_project: response.data.can_view_project || [],
        can_view_pictures: response.data.can_view_pictures || [],
        can_view_notes: response.data.can_view_notes || [],
        revoked_view_project: response.data.revoked_view_project || [],
        revoked_view_pictures: response.data.revoked_view_pictures || [],
        revoked_view_notes: response.data.revoked_view_notes || []
      });
    } catch (error) {
      toast.error('Failed to fetch permissions');
    }
  };

  // Employees have full access by default; checkbox reflects "not revoked" for them
  const revKey = (type) => type.replace('can_', 'revoked_');

  const isChecked = (user, permissionType) =>
    user.role === 'employee'
      ? !permissions[revKey(permissionType)].includes(user.id)
      : permissions[permissionType].includes(user.id);

  const togglePermission = (user, permissionType) => {
    const key = user.role === 'employee' ? revKey(permissionType) : permissionType;
    setPermissions(prev => {
      const currentList = prev[key];
      const newList = currentList.includes(user.id)
        ? currentList.filter(id => id !== user.id)
        : [...currentList, user.id];

      return {
        ...prev,
        [key]: newList
      };
    });
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const toggleAllInCategory = (categoryUsers, permissionType, shouldCheck, isEmployeeCategory) => {
    const key = isEmployeeCategory ? revKey(permissionType) : permissionType;
    const addToList = isEmployeeCategory ? !shouldCheck : shouldCheck;
    setPermissions(prev => {
      const currentList = prev[key];
      const userIds = categoryUsers.map(u => u.id);
      const newList = addToList
        ? [...new Set([...currentList, ...userIds])]
        : currentList.filter(id => !userIds.includes(id));

      return {
        ...prev,
        [key]: newList
      };
    });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/projects/${projectId}/permissions`, permissions);
      toast.success('Permissions updated successfully');
      setIsOpen(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to update permissions');
    } finally {
      setLoading(false);
    }
  };

  // Group users by role
  const [sortBy, setSortBy] = useState(() => localStorage.getItem('permissions_sort') || 'az');

  const sortUsers = (list) => {
    const sorted = [...list];
    if (sortBy === 'az') sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    else if (sortBy === 'newest') sorted.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    else if (sortBy === 'oldest') sorted.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
    return sorted;
  };

  const groupedUsers = {
    employee: sortUsers(users.filter(u => u.role === 'employee')),
    client: sortUsers(users.filter(u => u.role === 'client')),
    sub_contractor: sortUsers(users.filter(u => u.role === 'sub_contractor')),
    supervisor: sortUsers(users.filter(u => u.role === 'supervisor'))
  };

  const categoryConfig = {
    employee: {
      label: 'Employees',
      icon: Users,
      color: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      headerColor: 'bg-blue-500/10 border-blue-500/20'
    },
    client: {
      label: 'Clients',
      icon: UserCheck,
      color: 'bg-green-500/20 text-green-400 border-green-500/30',
      headerColor: 'bg-green-500/10 border-green-500/20'
    },
    sub_contractor: {
      label: 'Sub-Contractors',
      icon: Briefcase,
      color: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
      headerColor: 'bg-indigo-500/10 border-indigo-500/20'
    },
    supervisor: {
      label: 'Supervisors',
      icon: Shield,
      color: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      headerColor: 'bg-purple-500/10 border-purple-500/20'
    }
  };

  const renderCategorySection = (category, categoryUsers) => {
    if (categoryUsers.length === 0) return null;
    
    const config = categoryConfig[category];
    const Icon = config.icon;
    const isExpanded = expandedCategories[category];
    const isEmployeeCategory = category === 'employee';

    // Check if all users in category have a permission
    const allHaveViewProject = categoryUsers.every(u => isChecked(u, 'can_view_project'));
    const allHaveViewPictures = categoryUsers.every(u => isChecked(u, 'can_view_pictures'));
    const allHaveViewNotes = categoryUsers.every(u => isChecked(u, 'can_view_notes'));

    return (
      <div key={category} className="border border-gray-200 rounded-lg overflow-hidden">
        {/* Category Header */}
        <div 
          className={`flex items-center justify-between p-3 cursor-pointer ${config.headerColor} border-b border-gray-200`}
          onClick={() => toggleCategory(category)}
        >
          <div className="flex items-center gap-3">
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-500" />
            )}
            <div className={`p-1.5 rounded ${config.color}`}>
              <Icon className="w-4 h-4" />
            </div>
            <span className="font-semibold text-gray-900">{config.label}</span>
            <span className="text-sm text-gray-500">({categoryUsers.length})</span>
            {isEmployeeCategory && (
              <span className="text-[10px] sm:text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200" data-testid="employee-full-access-badge">
                Full access — uncheck to revoke
              </span>
            )}
          </div>
          
          {/* Quick toggle all buttons */}
          <div className="hidden sm:flex items-center gap-4 text-xs" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-1.5">
              <span className="text-gray-500">All:</span>
              <Checkbox
                checked={allHaveViewProject}
                onCheckedChange={(checked) => toggleAllInCategory(categoryUsers, 'can_view_project', checked, isEmployeeCategory)}
                className="scale-90"
              />
              <span className="text-gray-500">Project</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Checkbox
                checked={allHaveViewPictures}
                onCheckedChange={(checked) => toggleAllInCategory(categoryUsers, 'can_view_pictures', checked, isEmployeeCategory)}
                className="scale-90"
              />
              <span className="text-gray-500">Pictures</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Checkbox
                checked={allHaveViewNotes}
                onCheckedChange={(checked) => toggleAllInCategory(categoryUsers, 'can_view_notes', checked, isEmployeeCategory)}
                className="scale-90"
              />
              <span className="text-gray-500">Notes</span>
            </div>
          </div>
        </div>

        {/* Users List */}
        {isExpanded && (
          <div className="divide-y divide-gray-200">
            {categoryUsers.map((user) => (
              <div key={user.id} className="p-3 hover:bg-gray-50">
                {/* Desktop View */}
                <div className="hidden sm:flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 text-sm">{user.name}</div>
                    <div className="text-xs text-gray-500 truncate">{user.email}</div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={isChecked(user, 'can_view_project')}
                        onCheckedChange={() => togglePermission(user, 'can_view_project')}
                      />
                      <span className="text-xs text-gray-500 w-14">Project</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={isChecked(user, 'can_view_pictures')}
                        onCheckedChange={() => togglePermission(user, 'can_view_pictures')}
                      />
                      <span className="text-xs text-gray-500 w-14">Pictures</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={isChecked(user, 'can_view_notes')}
                        onCheckedChange={() => togglePermission(user, 'can_view_notes')}
                      />
                      <span className="text-xs text-gray-500 w-12">Notes</span>
                    </div>
                  </div>
                </div>

                {/* Mobile View */}
                <div className="sm:hidden space-y-2">
                  <div>
                    <div className="font-medium text-gray-900 text-sm">{user.name}</div>
                    <div className="text-xs text-gray-500 truncate">{user.email}</div>
                  </div>
                  <div className="flex items-center gap-4 pt-1">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <Checkbox
                        checked={isChecked(user, 'can_view_project')}
                        onCheckedChange={() => togglePermission(user, 'can_view_project')}
                        className="scale-90"
                      />
                      <span className="text-xs text-gray-700">Project</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <Checkbox
                        checked={isChecked(user, 'can_view_pictures')}
                        onCheckedChange={() => togglePermission(user, 'can_view_pictures')}
                        className="scale-90"
                      />
                      <span className="text-xs text-gray-700">Pictures</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <Checkbox
                        checked={isChecked(user, 'can_view_notes')}
                        onCheckedChange={() => togglePermission(user, 'can_view_notes')}
                        className="scale-90"
                      />
                      <span className="text-xs text-gray-700">Notes</span>
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const totalUsers = users.length;
  const hasAnyUsers = totalUsers > 0;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button className="bg-gradient-to-r from-purple-500 to-purple-600 text-sm sm:text-base">
          <Shield className="w-4 h-4 mr-2" />
          <span className="hidden sm:inline">Manage Permissions</span>
          <span className="sm:hidden">Permissions</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white text-gray-900 border-gray-200 max-w-[94vw] sm:max-w-3xl max-h-[85vh] overflow-y-auto mx-1 sm:mx-0 p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl flex items-center gap-2">
            <Shield className="w-5 h-5 text-purple-400" />
            Manage Permissions
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-gray-500">
              Select user access for this project. Admins and employees have full access by default — uncheck an employee to revoke.
            </p>
            <select
              value={sortBy}
              onChange={(e) => { setSortBy(e.target.value); localStorage.setItem('permissions_sort', e.target.value); }}
              className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white text-gray-800 flex-shrink-0"
              data-testid="permissions-sort-select"
            >
              <option value="az">A → Z</option>
              <option value="newest">Newest added</option>
              <option value="oldest">Oldest added</option>
            </select>
          </div>

          {!hasAnyUsers ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No users available to manage</p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Render each category */}
              {renderCategorySection('employee', groupedUsers.employee)}
              {renderCategorySection('sub_contractor', groupedUsers.sub_contractor)}
              {renderCategorySection('client', groupedUsers.client)}
              {renderCategorySection('supervisor', groupedUsers.supervisor)}
            </div>
          )}

          <div className="flex flex-col gap-2.5 pt-4 border-t border-gray-200">
            <Button 
              onClick={handleSave} 
              className="w-full h-11 text-sm sm:text-base bg-gradient-to-r from-green-500 to-green-600"
              disabled={loading}
            >
              <Check className="w-4 h-4 mr-2" />
              {loading ? 'Saving...' : 'Save Permissions'}
            </Button>
            <Button 
              onClick={() => setIsOpen(false)} 
              variant="outline"
              className="w-full h-11 text-sm sm:text-base border-gray-300 text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PermissionsDialog;
